import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { IStatistic2ReportParameter } from '../../statistic2';
import { Statistic2DisplayType } from '../../statistic2-types';
import { Statistic2HelperService } from '../../statistic2-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IStatistic2Report } from '@fi-app/statistic2/statistic2';
import { cloneDeep, FiDateFormatOptions, isDefined } from '@fi-shared/utils';
import { Statistic2ReportParameterEditDialogComponent } from '@fi-app/statistic2/statistic2-report-parameter-edit/statistic2-report-parameter-edit-dialog/statistic2-report-parameter-edit-dialog.component';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { Statistic2Service } from '@fi-app/statistic2/statistic2.service';
import { Statistic2DataRepresentationType, Statistic2ReportType } from '@fi-app/statistic2/statistic2-types';
import { takeUntil } from 'rxjs/operators';
import { ChartOptions } from 'chart.js';
import { IChartClickEvent } from '@fi-shared/fi-chart/fi-chart';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-statistic2-widget-item',
    templateUrl: './statistic2-widget-item.component.html',
    styleUrls: ['./statistic2-widget-item.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-statistic2-widget-item' },
    standalone: false,
})
export class Statistic2WidgetItemComponent implements OnInit, OnChanges, OnDestroy {
    @Input() reportParameter: IStatistic2ReportParameter;
    @Input() reportData: IStatistic2Report;
    @Input() isItemDropped: Observable<void>;

    menuActions = [
        {
            title: 'statistic.edit_widget',
            disabled: this.detectDeviceService.isMobileSmall(),
            onClick: () => {
                this.openEditReportDialog(this.reportParameter);
            },
        },
        {
            title: 'statistic.remove_widget',
            disabled: false,
            onClick: () => {
                this.statistic2HelperService.showRemoveWidgetFromDashboardDialog().onYesButtonClick(() => {
                    this.deleteWidget();
                });
            },
        },
    ];

    DisplayTypes = Statistic2DisplayType;
    DataRepresentationType = Statistic2DataRepresentationType;
    chartData: number[];
    chartLabels: string[];
    chartOptions: ChartOptions;
    loading$ = new BehaviorSubject<boolean>(true);
    periodText: string = '';
    triggerRecalculateChartWidth$ = new Subject<void>();

    constructor(
        private statistic2HelperService: Statistic2HelperService,
        private detectDeviceService: DetectDevicesService,
        private fiLoadingService: LoadingService,
        private statistic2Service: Statistic2Service,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit(): void {
        if (!isDefined(this.reportParameter.DataRepresentationTypeView)) {
            this.reportParameter.DataRepresentationTypeView = this.reportParameter.DataRepresentationType;
        }
        this.isItemDropped.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.triggerRecalculateChartWidth$.next();
        });
    }

    openEditReportDialog(reportParameter: IStatistic2ReportParameter) {
        this.statistic2HelperService
            .showEditReportDialog(
                'statistic.edit_statistics_report',
                Statistic2ReportParameterEditDialogComponent,
                cloneDeep(reportParameter),
            )
            .onYesButtonClick((editedReportParameter) => {
                editedReportParameter.DataRepresentationTypeView = editedReportParameter.DataRepresentationType;
                this.fiLoadingService.trackPromise(this.statistic2Service.saveReportParameter(editedReportParameter));
            });
    }

    deleteWidget() {
        this.reportParameter.ShowOnDashboard = false;
        this.fiLoadingService.trackPromise(this.statistic2Service.saveReportParameter(this.reportParameter));
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.reportData;
        if (change && change.currentValue) {
            this.buildChartData();
            this.buildChartOptions();
            this.loading$.next(false);
            if (this.reportData.StartDate && this.reportData.EndDate) {
                this.periodText =
                    this.fiFormatDateService.formatDate(this.reportData.StartDate, FiDateFormatOptions) +
                    ' - ' +
                    this.fiFormatDateService.formatDate(this.reportData.EndDate, FiDateFormatOptions);
            }
        }
    }

    getReportDescription() {
        const str =
            this.statistic2HelperService.getReportTypeText(this.reportParameter.ReportType) +
            ', ' +
            this.appTextService.get('statistic.for') +
            ' ' +
            this.statistic2HelperService.getReportForText(this.reportParameter) +
            ', ' +
            this.appTextService.get('statistic.per') +
            ' ' +
            this.statistic2HelperService.getShowResultsPerText(this.reportParameter.ShowResultsPerType)?.toLowerCase();
        return str;
    }

    showPieChartDisplayType() {
        return this.statistic2HelperService.showPieChartDisplayType(this.reportParameter);
    }

    getChartType() {
        if (this.reportParameter.DisplayType === Statistic2DisplayType.BarChart) return 'bar';
        if (this.reportParameter.DisplayType === Statistic2DisplayType.PieChart) return 'doughnut';
        if (this.reportParameter.DisplayType === Statistic2DisplayType.LineGraph) return 'line';
        return 'bar'; //todo default value
    }

    onDisplayTypeChanged() {
        this.buildChartOptions();
    }

    onDataRepresentationTypeChange() {
        if (
            this.reportParameter.DisplayType === this.DisplayTypes.PieChart &&
            this.reportParameter.DataRepresentationTypeView === this.DataRepresentationType.Percentage
        ) {
            this.reportParameter.DisplayType = this.DisplayTypes.BarChart;
        }
        this.buildChartData();
        this.buildChartOptions();
        this.statistic2Service.saveReportParameter(this.reportParameter, true);
    }

    openExtendedReport() {
        this.statistic2HelperService.openExtendedReportView(this.reportParameter);
    }

    private buildChartData() {
        this.reportParameter.DataRepresentationTypeView === this.DataRepresentationType.Percentage
            ? (this.chartData = this.reportData.Data.map((item) => item.ValuePercentage))
            : (this.chartData = this.reportData.Data.map((item) => item.Value));
        this.chartLabels = this.reportData.Data.map((item) => item.ObjectName);
    }

    isShowDataPresentationType() {
        return this.statistic2HelperService.isShowDataPresentationType(this.reportParameter);
    }

    private buildChartOptions() {
        // todo implement labels
        this.chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
        };
        if (
            this.reportParameter.DisplayType === Statistic2DisplayType.BarChart ||
            this.reportParameter.DisplayType === Statistic2DisplayType.LineGraph
        ) {
            const customChartOptions: ChartOptions = {
                scales: {
                    y: {
                        ticks: {
                            callback: (value: number) => {
                                return Math.abs(Math.floor(value) - value) < 0.001 ? Math.floor(value) : '';
                            },
                        },
                        beginAtZero: true, // minimum value will be 0.
                    },
                },
            };
            this.chartOptions = Object.assign(this.chartOptions, customChartOptions);
        }
        if (this.reportParameter.DisplayType === Statistic2DisplayType.LineGraph) {
            const customChartOptions: ChartOptions = {
                elements: {
                    line: {
                        fill: false,
                    },
                    point: {
                        radius: 5,
                    },
                },
            };
            this.chartOptions = Object.assign(this.chartOptions, customChartOptions);
        }
        if (this.reportParameter.ReportType === Statistic2ReportType.ReadRating) {
            const customChartOptions: ChartOptions = {
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => this.appTextService.get('statistic.read_rating'),
                            label: (label) => {
                                return `${this.chartLabels[label.dataIndex]}: ${label.raw}%`;
                            },
                        },
                    },
                    legend: {
                        display: false,
                    },
                },
                scales: {
                    y: {
                        ticks: {
                            callback: (value) => value + '%',
                        },
                        beginAtZero: true,
                    },
                },
            };
            this.chartOptions = Object.assign(this.chartOptions, customChartOptions);
        }
        if (
            this.reportParameter.ReportType === Statistic2ReportType.UserLogins &&
            this.reportParameter.DisplayType !== Statistic2DisplayType.PieChart
        ) {
            const customChartOptions: ChartOptions = {
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => this.appTextService.get('statistic.user_logins'),
                            afterTitle: () => this.periodText,
                            label: (label) => `${label.label}: ${label.raw}`,
                        },
                    },
                    legend: {
                        display: false,
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: this.appTextService.get('statistic.user_logins'),
                        },
                    },
                },
            };
            this.chartOptions = Object.assign(this.chartOptions, customChartOptions);
        }
        if (this.reportParameter.DataRepresentationTypeView === Statistic2DataRepresentationType.Percentage) {
            const customChartOptions: ChartOptions = {
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => this.periodText,
                            afterTitle: (afterTitle) => afterTitle[0].label,
                            label: (label) => `${this.appTextService.get('statistic.user_logins')}: ${label.raw}%`,
                        },
                    },
                    legend: {
                        display: false,
                    },
                },
                scales: {
                    y: {
                        ticks: {
                            display: true,
                            callback: (value) => {
                                return value + '%';
                            },
                        },
                        title: {
                            display: true,
                            text: this.appTextService.get('statistic.user_logins'),
                        },
                        beginAtZero: true,
                    },
                },
            };

            this.chartOptions = Object.assign(this.chartOptions, customChartOptions);
        }
        /*const customChartOptions: ChartOptions = {
            scales:{ // todo looks like we don't need this
                x: {
                    ticks: {
                        callback: (tick: string) => {
                            if (tick.length > 15) {
                                return tick.substr(0, 15) + '...'
                            } else return tick;
                        }
                    }
                }
            }
        }
        this.chartOptions = Object.assign(this.chartOptions, customChartOptions);*/

        if (
            this.reportParameter.ReportType === Statistic2ReportType.UserLogins &&
            this.reportParameter.DisplayType === Statistic2DisplayType.PieChart
        ) {
            const customChartOptions: ChartOptions = {
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: () => this.appTextService.get('statistic.user_logins'),
                            afterTitle: () => this.periodText,
                            /*label: (label, data) => { //todo
                                if (this.reportParameter.DataRepresentationTypeView === Statistic2DataRepresentationType.Percentage) {
                                    return `${data.labels[label.index]}: ${data.datasets[0].data[label.index]}%`
                                } else {
                                    return `${data.labels[label.index]}: ${data.datasets[0].data[label.index]}`
                                }
                            }*/
                        },
                    },
                },
                scales: {
                    x: {
                        display: false,
                    },
                },
            };

            this.chartOptions = Object.assign(this.chartOptions, customChartOptions);
        }
        if (this.reportParameter.DisplayType === Statistic2DisplayType.PieChart) {
            const customChartOptions: ChartOptions = {
                responsive: true,
                plugins: {
                    legend: {
                        position: 'right',
                    },
                },
                /*legend: false, // todo
                legendCallback: (chart) => {
                    let legendHtml = '<ul class="chart-legend">';
                    let item = chart.config.data.datasets[0];

                    for (let i = 0; i < item.data.length; i++) {
                        legendHtml += `
                            <li class="chart-legend-item">
                                <div class="chart-legend-item-color" style="background-color:'${item.backgroundColor[i]}'">${item.backgroundColor[i]}</div>
                                <span class="chart-legend-item-label">${chart.config.data.labels[i]}</span>
                            </li>
                        `;
                    };
                    legendHtml += '</ul>';
                    return legendHtml;
                }*/
            };

            this.chartOptions = Object.assign(this.chartOptions, customChartOptions);
        }
        this.triggerRecalculateChartWidth$.next();
    }

    isPieChart() {
        return this.reportParameter.DisplayType === Statistic2DisplayType.PieChart;
    }

    onChartClick($event: IChartClickEvent) {
        if ($event.active.length) {
            let selectedReportItemIndex = ($event.active[0] as any).index;
            this.statistic2HelperService.setSelectedReportItemIndex(selectedReportItemIndex);

            this.openExtendedReport();
        }
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
